/**
 * La documentación técnica en la ficha del artículo: fichas, certificados y
 * manuales que subió quien lo distribuye.
 *
 * Es lo que faltaba para que la ficha sirva sin llamar por teléfono, que es
 * justamente el costo administrativo que la plataforma pretende eliminar. El
 * proveedor podía subir el datasheet desde el primer día y nadie fuera de él y
 * del equipo lo veía: `filtroArchivos` le daba al cliente solo lo público —que
 * son las fotos— y la ficha ni siquiera pedía archivos.
 *
 * Dos decisiones que se ven en esta pantalla:
 *
 *  · **La lista es pública y la descarga exige sesión.** Saber que hay ficha
 *    técnica ayuda a decidir y no cuesta ancho de banda; bajar 12 MB, sí. Es el
 *    mismo criterio con que ya se reservan el stock exacto y el plazo de
 *    entrega. El servidor lo dice en `descargable` y acá se ofrece "entrar"
 *    en vez de un enlace que responde 401 en una pestaña nueva.
 *
 *  · **El enlace apunta a la API, no al almacén.** La clave del objeto empieza
 *    por el id del proveedor, así que una URL firmada se lo pondría al cliente
 *    en la barra de direcciones. El nombre del archivo también lo compone el
 *    servidor: el "Ferretería XYZ - ficha 6200.pdf" que subió el distribuidor
 *    no llega nunca.
 *
 * Se abre en pestaña nueva y no en un `iframe` porque la CSP declara
 * `frame-ancestors: 'none'`: un visor incrustado quedaría en blanco, y sin
 * error visible.
 */
import { Link } from 'react-router-dom'
import { formatearBytes } from '@compranorte/domain'
import { urlDocumento, useDocumentosArticulo } from '../api/hooks'
import { useSesion } from '../auth/sesion'
import { IconoDocumento, IconoVer } from './icons'
import { Panel } from './ui'
import { fecha } from '../lib/format'

export function DocumentacionArticulo({ articuloId }: { articuloId: string }) {
  const { yo } = useSesion()
  const consulta = useDocumentosArticulo(articuloId)

  /*
   * Mientras carga no se dibuja nada, y si falla tampoco.
   *
   * La documentación es un complemento de la ficha: un esqueleto o un mensaje
   * de error acá le daría peso visual a lo que puede no existir, y la mayoría
   * de los artículos no va a tener documentos hasta que los proveedores los
   * suban. Lo que no se puede es fingir: si hay documentos, se ven; si no se
   * pudieron pedir, no se dice que no hay.
   */
  if (consulta.isPending || consulta.isError) return null

  const { datos, descargable } = consulta.data
  if (datos.length === 0) return null

  return (
    <Panel titulo="Documentación técnica" padding={false}>
      <ul className="divide-y divide-acero-200">
        {datos.map((d) => {
          const fila = (
            <>
              <IconoDocumento size={16} className="shrink-0 text-acero-500" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-acero-900">{d.etiqueta}</span>
                <span className="block text-[11.5px] text-acero-500">
                  {/* El peso importa: 12 MB en la conectividad de una faena es
                      otra decisión que 200 KB. */}
                  PDF · <span className="num">{formatearBytes(d.bytes)}</span> ·{' '}
                  {fecha(d.creadoEn)}
                </span>
              </span>
            </>
          )

          return (
            <li key={d.id} className="px-3 py-2.5 text-[12.5px]">
              {descargable ? (
                <a
                  href={urlDocumento(articuloId, d.id)}
                  target="_blank"
                  // `noopener` con `_blank` no es opcional: sin él la pestaña
                  // nueva conserva una referencia a esta ventana.
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 hover:text-marca-600 group"
                >
                  {fila}
                  <IconoVer
                    size={15}
                    className="shrink-0 text-acero-400 group-hover:text-marca-600"
                  />
                </a>
              ) : (
                <div className="flex items-center gap-2.5 text-acero-600">
                  {fila}
                  {/*
                    `descargable` es falso por dos motivos distintos y no da lo
                    mismo cuál: sin sesión hay algo que hacer —entrar—, y con
                    sesión el almacén está apagado en este entorno y no hay
                    nada que la persona pueda hacer. Ofrecerle "entrar" a quien
                    ya entró lo manda a un login que lo devuelve al mismo lugar.
                  */}
                  {yo ? (
                    <span className="shrink-0 text-[11.5px] text-acero-500">no disponible</span>
                  ) : (
                    <Link
                      to="/entrar"
                      state={{ volverA: `/producto/${articuloId}` }}
                      className="shrink-0 text-[11.5px] text-marca-600 hover:underline"
                    >
                      Entrar para verla
                    </Link>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {!descargable && !yo && (
        <p className="ayuda px-3 pb-3">
          La documentación técnica está disponible para clientes registrados.
        </p>
      )}
    </Panel>
  )
}
